/*
 * Floor Lamp Picker — product-form cart bridge
 * Danny Lim (Sleek App)
 *
 * The "selection only" picker block (snippets/floor-lamp-picker-right.liquid)
 * has no buy buttons of its own. This wires the product page's own controls to
 * the customer's configured lamp:
 *
 *   LÆG I KURV  → adds base + 3 shades (+ optional bulbs) to the cart
 *   KØB NU      → same, then redirects to /checkout
 *   Quantity    → multiplies the whole bundle (qty 2 = 2 bases + 6 shades)
 *
 * The page's OWN variant is deliberately NOT added: the picker already includes
 * "Vælg lampefod", so adding the page variant too would duplicate the base.
 *
 * ── Why KØB NU is replaced rather than intercepted ──────────────────────────
 * The theme renders Shopify's dynamic checkout button via {{ form | payment_button }}.
 * That button posts the form straight to checkout carrying only the form's own
 * variant + quantity; there is no supported way to attach extra line items to it.
 * So when this block is present we hide it and substitute a button that adds the
 * bundle to the cart first, then sends the customer to /checkout. This is scoped
 * to lamp-picker products only — every other product keeps the native button and
 * its express wallets (Apple Pay / Google Pay / Shop Pay).
 *
 * ── Why the submit hook is a capture listener on document ───────────────────
 * assets/product-form.js binds its own 'submit' handler directly to the form.
 * Listeners on the same element fire in registration order, so registering
 * another one on the form is a race. A capture-phase listener on `document`
 * always runs before the event reaches the form, letting us stopPropagation()
 * and take over deterministically without editing product-form.js.
 */
(function () {
  'use strict';

  var CHECKOUT_URL = '/checkout';

  function picker() {
    return window.FloorLampPickerRight || null;
  }

  /*
   * True while the customer isn't building a bundle — nothing selected, or only
   * the slot pre-filled from this product page's own product. They're just
   * buying the product in front of them, so this bridge must not intervene:
   * no bundle, no discount codes, and crucially no "vælg lampefod og alle tre
   * skærme" guard blocking a perfectly ordinary purchase. Danny Lim (Sleek App).
   */
  function pickerUnconfigured() {
    var api = picker();
    return !!(api && typeof api.isUnconfigured === 'function' && api.isUnconfigured());
  }

  /*
   * TODO — PORT TO THE FODGÆNGER PICKER (lives on its own branch, not on dev).
   *
   * This file is shared: sections/main-product.liquid loads it for BOTH the
   * floor_lamp_picker and fodgaenger_lamp_picker blocks, so the passthrough
   * above already applies to the Fodgænger picker too. What is missing there is
   * the other half of the contract — snippets/fodgaenger-lamp-picker-right.liquid
   * must expose the same method on its window.FloorLampPickerRight API:
   *
   *   isUnconfigured: function () {
   *     if (!FLP_PRODUCT_HANDLE) return false;
   *     var filled = [];
   *     ['base', 'left', 'middle'].forEach(function (key) {      // no 'right' — 2-armed
   *       var p = selectedProducts[key];
   *       if (p && p.handle && p.handle !== 'none') filled.push(p.handle);
   *     });
   *     if (filled.length === 0) return true;
   *     return filled.length === 1 && filled[0] === FLP_PRODUCT_HANDLE;
   *   },
   *
   * Copy it from snippets/floor-lamp-picker-right.liquid, dropping 'right' from
   * the slot list (the Fodgænger lamp is 2-armed). Without it,
   * pickerUnconfigured() returns false there and the Fodgænger product pages
   * keep the old "select everything first" dead end. Danny Lim (Sleek App).
   */

  /* The block only renders on lamp-picker products, so its presence is the
     signal that this page's buy buttons belong to a configured bundle. */
  function pickerPresent() {
    return !!document.querySelector('.flp-right-block') && !!picker();
  }

  function addUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function readQuantity(form) {
    var input = form && form.querySelector('input[name="quantity"]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  function setBusy(el, busy) {
    if (!el) return;
    el.classList.toggle('flp-busy', !!busy);
    if (busy) {
      el.setAttribute('aria-busy', 'true');
      el.setAttribute('disabled', 'disabled');
    } else {
      el.removeAttribute('aria-busy');
      el.removeAttribute('disabled');
    }
  }

  function cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  /*
   * Adds the configured bundle. Resolves with the parsed /cart/add response.
   * Rejects if the selection is incomplete or Shopify returns an error, so the
   * caller can decide whether to open the drawer or navigate to checkout.
   */
  async function addBundleToCart(quantity) {
    var api = picker();
    if (!api) throw new Error('Lampevælgeren er ikke klar.');

    if (!api.isComplete()) {
      api.showIncompleteError();
      throw new Error('INCOMPLETE');
    }

    var items = api.getItems();
    if (!items.length) throw new Error('Ingen gyldige varer at tilføje.');

    // Quantity multiplies the whole bundle. The bulb line already carries its
    // own per-lamp quantity (default 3), so it scales too.
    if (quantity > 1) {
      items = items.map(function (item) {
        return { id: item.id, quantity: item.quantity * quantity };
      });
    }

    var body = new FormData();
    items.forEach(function (item, i) {
      body.append('items[' + i + '][id]', item.id);
      body.append('items[' + i + '][quantity]', item.quantity);
    });

    // Match the original section: tag the cart so downstream reporting can tell
    // these lines came from the lamp selector.
    var drawer = cartDrawer();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.append('sections', drawer.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
    }

    var res = await fetch(addUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    });
    var data = await res.json();
    if (!res.ok || data.status) {
      // Always log the raw response, even when it falls back to the generic
      // message below — Shopify's error shape varies (some rejections carry
      // description/message, some don't), so this is the fastest way to see
      // what actually happened without reopening DevTools mid-repro.
      console.error('[FLP cart] /cart/add rejected', { status: res.status, body: data, itemsSent: items });
      throw new Error(data.description || data.message || 'Kunne ikke lægge i kurven.');
    }

    // Tag the cart for the "Lamp Selector" order tag — only now that the items
    // are actually in it, and only with the picker's own verdict. This used to
    // run before /cart/add and always wrote 'true', which tagged carts whose
    // add then failed and tagged non-qualifying adds (a complete lamp earning
    // only the bulb code, say). getTagAttributes() writes '' to clear the flag
    // when nothing qualifies. Danny Lim (Sleek App).
    var tagAttributes = typeof api.getTagAttributes === 'function'
      ? api.getTagAttributes()
      : { lamp_selector: '', lamp_selector_bundle: '' };
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: tagAttributes })
      });
    } catch (e) {
      // Non-fatal: the attribute is for reporting, not for the sale.
      console.warn('[FLP cart] could not set cart attribute', e);
    }

    // Bundle / bulb discount codes, applied through the picker's own routine so
    // the iframe cookie trick and its verification stay in one place.
    var codes = api.getDiscountCodes();
    if (codes.length) {
      try {
        await api.applyDiscountCodes(codes);

        // Re-fetch the cart sections AFTER the codes are on the cart.
        // `data` above came from /cart/add, which ran BEFORE the discount
        // existed — rendering it would show the customer an un-discounted
        // total and read as "the discount didn't apply", even though it did.
        // This mirrors what the original section does post-apply.
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          var refreshed = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: tagAttributes,
              sections: drawer.getSectionsToRender().map(function (s) { return s.id; }).join(','),
              sections_url: window.location.pathname
            })
          });
          if (refreshed.ok) {
            var refreshedData = await refreshed.json();
            if (refreshedData && refreshedData.sections) data = refreshedData;
          }
        }
      } catch (e) {
        console.warn('[FLP cart] discount codes failed to apply', e);
      }
    } else {
      console.info('[FLP cart] no discount codes qualified for this selection');
    }

    return data;
  }

  function showError(message) {
    var box = document.querySelector('.cart-error-message');
    if (box) {
      box.textContent = message;
      box.style.display = 'block';
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.error('[FLP cart]', message);
    }
  }

  /* ---------------- LÆG I KURV ---------------- */

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      if (!pickerPresent()) return; // ordinary product — leave the theme alone
      // Untouched picker: this is a plain single-product purchase. Do not
      // preventDefault, so product-form.js adds just this variant as normal.
      if (pickerUnconfigured()) return;

      event.preventDefault();
      event.stopPropagation();          // keeps product-form.js from also firing
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      var submitBtn = form.querySelector('[name="add"]');
      setBusy(submitBtn, true);

      addBundleToCart(readQuantity(form))
        .then(function (data) {
          var drawer = cartDrawer();
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data);
          } else {
            // No drawer (or no section payload) — fall back to the cart page so
            // the customer still sees what happened.
            window.location = '/cart';
          }
        })
        .catch(function (err) {
          if (err && err.message !== 'INCOMPLETE') showError(err.message);
        })
        .finally(function () {
          setBusy(submitBtn, false);
        });
    },
    true // capture — must beat product-form.js
  );

  /* ---------------- KØB NU ---------------- */

  /*
   * Keeps the two Køb nu buttons in sync with the picker's state:
   *   untouched  -> native dynamic checkout button (Apple Pay / Shop Pay etc.),
   *                 buying just this product, exactly as any other page
   *   configured -> our substitute, which adds the bundle then goes to checkout
   * Re-run on selection changes so switching between the two is seamless.
   */
  function installBuyNow() {
    if (!pickerPresent()) return;

    var host = document.querySelector('.product-form__checkout');
    if (!host) return;

    var native = host.querySelector('.shopify-payment-button');
    var mine = host.querySelector('.flp-buy-now');

    if (pickerUnconfigured()) {
      // Plain single-product purchase — hand the page back to the theme.
      if (mine) mine.remove();
      if (native) native.style.display = '';
      return;
    }

    // Hide the native dynamic checkout button — it cannot carry our line items.
    if (native) native.style.display = 'none';
    if (mine) return;

    var form = document.querySelector('form[data-type="add-to-cart-form"]');

    var btn = document.createElement('button');
    btn.type = 'button';
    // Reuse the theme's own unbranded-checkout styling so it looks identical.
    btn.className = 'flp-buy-now shopify-payment-button__button shopify-payment-button__button--unbranded';
    btn.textContent = 'Køb nu';

    btn.addEventListener('click', function () {
      setBusy(btn, true);
      addBundleToCart(readQuantity(form))
        .then(function () { window.location = CHECKOUT_URL; })
        .catch(function (err) {
          if (err && err.message !== 'INCOMPLETE') showError(err.message);
          setBusy(btn, false);
        });
    });

    host.appendChild(btn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installBuyNow);
  } else {
    installBuyNow();
  }
  // The payment button is injected asynchronously by Shopify's SDK, and the
  // theme editor re-renders sections in place — re-run on both.
  window.addEventListener('load', installBuyNow);
  document.addEventListener('shopify:section:load', installBuyNow);

  /* The customer can start (or undo) a configuration at any time, so re-evaluate
     which Køb nu button belongs on the page after any interaction with the
     picker. Debounced because the picker re-renders its menu on each change. */
  var buyNowSyncTimer = null;
  function scheduleBuyNowSync(event) {
    if (!event.target || !event.target.closest || !event.target.closest('.flp-right-block')) return;
    clearTimeout(buyNowSyncTimer);
    buyNowSyncTimer = setTimeout(installBuyNow, 120);
  }
  document.addEventListener('click', scheduleBuyNowSync, true);
  document.addEventListener('change', scheduleBuyNowSync, true);
})();
